import React, { useState, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  ChevronUp,
  ChevronDownUnbolded,
  Success,
  CloseSmall
} from '../../icons';
import DropArea, { SelectFiles } from '../../components/uploadModal/DropArea';
import spinnerLight from '../../assets/images/spinner-light.svg';
import { simpleGTMDataLayer } from '../../util/GTMHelpers';
import { DocumentBox, DocumentName, DocumentText, Label } from '../../components/changePolicy/changePolicyStyles';

export interface modalProperties {
  staging: boolean;
  showModalHandler: Function;
  stagingHandler: Function;
  readOnlyAccess: boolean;
  readOnlyFeatureFlag: boolean;
  uploadToken: string;
  uploadDoc: Function;
  sourceSystemCode: string;
  quoteNo: string | null;
}

const Container = styled.div`
  z-index: 2;
  display: inline;
  position: absolute;
  margin: auto;
  align-self: center;
`;
const StaginModalContainer = styled.div`
  display: block;
  flex-direction: row;
  z-index: 1;
  max-width: 1000px;
  margin: auto;
  height: 600px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const StagingModal = styled.div`
  height: 280px;
  position: relative;
  display: flex;
  flex-direction: column;
  margin: auto;
  border: 1px, solid, #979797;
  box-shadow: 0px 8px 32px -8px #002038;
  padding: 30px;
  background-color: #ffffff;
  align: center;
  justify: center;
  max-width: 1000px;
  align-content: center;
  justify-content: center;
`;
const UploadModalContainer = styled.div`
  display: inline;
  flex-direction: row;
  z-index: 1;
  left: 0;
  top: 0;
  max-width: 650px;
  margin: auto;
  max-height: 497px;
`;
const UploadModal = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin: auto;
  border: 1px, solid, #979797;
  box-shadow: 0px 8px 32px -8px #002038;
  padding: 30px;
  background-color: #ffffff;
  align: center;
  justify: center;
  max-width: 650px;
  max-height: 497px;
`;
const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const HeaderText = styled.div`
  font-size: 24px;
  font-weight: 700;
`;
const SubtitleText = styled.span`
  font-size: 14px;
`;
const SubtitleTextNote = styled.span`
  font-size: 14px;
  color: #9325b2;
  font-weight: 700;
`;
const StagingBox = styled.div`
  border-radius: 4px;
  margin-top: 30px;
  overflow: auto;
  height: 300px;
`;
const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: 30px;
  vertical-align: middle;
`;
const FileControl = styled.div`
  display: flex;
  flex-direction: row;
  height: 30px;
`;
export const CancelAnchor = styled.div`
  font-size: 14px;
  font-weight: 700;
  justify: end;
  align: end;
  text-align: right;
  margin-top: 5px;
  margin-left: 20px;
  margin-right: 30px;
  &:hover {
    cursor: pointer;
  }
`;
export const SelectButton = styled.label`
  font-weight: 800;
  font-size: 14px;
  background-color: #0173c6;
  color: #ffffff;
  border-radius: 3px;
  border: 1px solid #0173c6;
  padding-top: 15px;
  padding-left: 40px;
  padding-right: 20px;
  height: 35px;
  width: 70px;
  &:hover {
    cursor: pointer;
  }
`;
export const UploadButton = styled.button`
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${props => (props.disabled ? '#bdbdbd' : '#ffffff')};
  font-weight: 700;
  font-size: 14px;
  background-color: #65a100;
  border-radius: 3px;
  border: 1px solid #65a100;
  padding-top: 7px;
  padding-bottom: 6px;
  padding-left: 20px;
  padding-right: 20px;
  margin-left: 15px;
`;

const StagingGap = styled.div`
  margin-right: 260px;
`;

export const ErrorText = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #e02020;
`;

const PolicyDropBox = styled.div`
  padding-left: 10px;
`;

const DocumentSelector = styled.div`
  background-color: #ffffff;
  color: #000000;
  border: 1px solid #d9dfeb;
  border-radius: 3px;
  margin-right: 10px;
  padding: 10px;
  padding-bottom: 0;
  width: 95%;
  height: 40%;
  position: relative;
`;
const DropDown = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-size: 18px;
  gap: 10px;
  width: 415px;
  &:hover {
    cursor: pointer;
  }
`;

const Content = styled.div`
  margin-left: 0px;
  padding-left: 10px;
  height: 40px;
  word-wrap: break-word;
  border: 1px solid #d9dfeb;
  background-color: #ffffff;
  z-index: 100;
  padding-top: 12px;
  &:hover {
    cursor: pointer;
  }
`;

export const CloseSizing = styled.div`
  height: 24px;
  &:hover {
    cursor: pointer;
  }
`;
const Options = styled.div`
  top: 100%;
  left: 0;
  margin-left: 0px;
  overflow: auto;
  max-height: 190px;
  word-wrap: break-word;
  padding-right: 10px;
  position: absolute;
  width: 100%;
`;

export default ({
  staging,
  showModalHandler,
  stagingHandler,
  readOnlyAccess,
  readOnlyFeatureFlag,
  uploadToken,
  uploadDoc,
  sourceSystemCode,
  quoteNo
}: modalProperties) => {
  const [openSend, setOpenSend] = useState(false);
  const [sendTo, setSendTo] = useState('Send Document To');
  const [securityType, setSecurityType] = useState('');
  const securityTypes = [
    ['sensitive', 'State Auto Only'],
    ['unrestricted', 'Customer & State Auto']
  ];
  const [securityError, setSecurityError] = useState(false);
  const [documentError, setDocumentError] = useState(false);
  const [uploadErr, setUploadErr] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [spinnerOn, uploadFlag] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadFail, setUploadFail] = useState(false);

  const getDocumentContents: Function = (f: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(f);
      reader.onload = () => resolve(reader.result);
      reader.onerror = err => {
        reject(err);
        return null;
      };
    });

  const uploadDocument: Function = async () => {
    setIsLoading(true);
    setUploadErr(false);
    setUploadFail(false);
    if (!file) setDocumentError(true);
    if (!securityType) setSecurityError(true);
    if ((!file || !securityType)) {
      setUploadErr(true);
      setIsLoading(false);
      return;
    }
    try {
      let documentData;
      if (file) documentData = await getDocumentContents(file);
      await uploadDoc({
        variables: {
          policy_number: quoteNo || "",
          file_name: file?.name,
          mime_type: file?.type,
          document_type: 'letter_received',
          security_type: securityType,
          source_system_code: sourceSystemCode === 'commercial' ? 'GWCL' : 'GWPL',
          document_data: documentData,
          query_env: 'l3-5',
          token: uploadToken || "",
          isQuote: true
        }
      }).then((response: any) => {
        setIsLoading(false);
        setUploadFail(false);
        if (response && response?.errors) {
          setUploadFail(true);
          console.log('Error uploading doc', response?.errors);
        }
        else {
          window.location.reload();
        }
      });
    } catch (err) {
      console.log('error uploading doc', err);
      setIsLoading(false);
      setUploadFail(true);
    } finally { setIsLoading(false); }
  };

  return (
    <Container id="Modal">
      {
        (
          <StaginModalContainer id="StagingModal">
            <StagingModal>
              <HeaderRow>
                <StagingGap>
                  <HeaderText>Upload Document</HeaderText>
                </StagingGap>
              </HeaderRow>
              {uploadErr && (<ErrorText>All fields are mandatory</ErrorText>)}
              {uploadFail && (<ErrorText>Unable to upload document, try again in some time.</ErrorText>)}
              <StagingBox>
                <DocumentBox>
                  <DocumentName
                    showError={false}
                    test-attr="policy-change-document-name"
                  >
                    <Label
                      active={true}
                      showError={false}
                    >
                      Document
                    </Label>
                    {file?.name ? (
                      <DocumentText test-attr="policy-change-file-name">
                        {file.name.length > 25
                          ? `${file.name.substring(0, 25)}...`
                          : file.name}
                      </DocumentText>
                    ) : (
                      <div />
                    )}
                  </DocumentName>
                  <SelectButton htmlFor="file">
                    <SelectFiles
                      type="file"
                      name="file"
                      id="file"
                      accept=".pdf, image/*"
                      onChange={e => {
                        const files: FileList | null | undefined =
                          e?.target?.files;
                        if (files && files.length) {
                          setFile(files[0]);
                        }
                      }}
                      test-attr="policy-change-file-input"
                    />
                    Browse
                  </SelectButton>
                </DocumentBox>
              </StagingBox>
              <DocumentSelector>
                {openSend && (
                  <Options>
                    {securityTypes.map(item => {
                      return (
                        <Content
                          id={item[0]}
                          onClick={() => {
                            setSendTo(item[1]);
                            setSecurityType(item[0]);
                            setSecurityError(false);
                            setOpenSend(false);
                          }}
                        >
                          {item[1]}
                        </Content>
                      );
                    })}
                  </Options>
                )}
                <DropDown
                  onClick={() => {
                    setOpenSend(!openSend);
                  }}
                >
                  {sendTo}
                  {openSend ? <ChevronUp /> : <ChevronDownUnbolded />}
                </DropDown>
              </DocumentSelector>
              <ButtonRow>
                <FileControl>
                  <CancelAnchor
                    id="CloseButton"
                    onClick={() => {
                      simpleGTMDataLayer({
                        event: 'uploadDocumentClick',
                        event_action: 'Button Click',
                        event_category: window.location.pathname,
                        event_label: 'Cancel Upload Documents'
                      });
                      showModalHandler(false);
                    }}
                  >
                    CLOSE
                  </CancelAnchor>
                  <UploadButton
                    id="UploadButton"
                    onClick={() => {
                      uploadDocument();
                      simpleGTMDataLayer({
                        event: 'uploadDocumentClick',
                        event_action: 'Button Click',
                        event_category: window.location.pathname,
                        event_label: 'Upload Documents'
                      });

                    }}
                    disabled={readOnlyAccess && readOnlyFeatureFlag || isLoading}
                  >
                    {isLoading ? 'UPLOADING...' : 'UPLOAD DOCUMENT'}
                    {spinnerOn !== 0 && <img src={spinnerLight} alt="spinner" />}
                  </UploadButton>
                </FileControl>
              </ButtonRow>
            </StagingModal>
          </StaginModalContainer>
        )

      }
    </Container >
  );
};
